// The quick tools' JSON API (FaceShot-ChopShop-web/routes/quicktools.js).
// Same-origin, no account: the 128-bit job id in the URL is the key.

export type QuickToolKey = 'captions' | 'faceless' | 'hooks' | 'canvas' | 'vows' | 'tapes';

export interface QuickToolConfig {
  price_cents: number;
  /** The same price in credits (10 cents a credit). */
  credits?: number;
  voices?: { value: string; label: string }[];
  voice_available?: boolean;
  /** The Living Cover: the whole-song version's price (dollars and credits), and whether the site mark is in place. */
  full_song_credits?: number;
  full_song_price_cents?: number;
  /** The price ladder (owner 2026-10-22): the loop with the 30 s song version, with the whole song, and the Full pack (both). */
  clip_price_cents?: number;
  clip_credits?: number;
  pack_price_cents?: number;
  pack_credits?: number;
  /** Lyrics on screen, an add-on to any song version (owner 2026-10-22). */
  lyrics_price_cents?: number;
  lyrics_credits?: number;
  /** Just a moving picture's one flat price, and the platforms an artist gets free before each costs extra (owner 2026-10-23). */
  picture_price_cents?: number;
  picture_credits?: number;
  platforms_free?: number;
  extra_platform_price_cents?: number;
  extra_platform_credits?: number;
  watermark_available?: boolean;
}

export interface QuickJob {
  id: string;
  tool: QuickToolKey;
  status: 'awaiting_payment' | 'queued' | 'processing' | 'ready' | 'failed';
  error: string | null;
  message: string | null;
  paid: boolean;
  position: number;
  price_cents: number;
  credits?: number;
  preview_url: string | null;
  preview_type: 'image' | 'video' | null;
  full_song?: boolean;
  song_seconds?: number;
  /** Extra paid files, downloaded with ?file=<name> (the Living Album Cover's 'song'). */
  extra_files?: string[];
  /** The shareable living cover page, once paid. */
  cover_url?: string | null;
  /** The site mark is on the file. */
  marked?: boolean;
  /** Paid and marked: POST .../remake makes it again without the mark, free. */
  can_unmark?: boolean;
  /** The song version's length as asked: 'clip' (best 30 s) or 'full'. */
  length?: string | null;
  /** The Living Cover's lyrics: 'synced' (timed to the vocals) or 'even' (spread over the singing). */
  lyrics?: { mode: 'synced' | 'even'; lines: number } | null;
  /** The release versions made (each downloads with ?file=release-<kind>). */
  releases?: string[];
  /** The Living Cover's shape (owner 2026-10-23): the loop's and the song versions' shape and size, and every shape made. */
  shape?: string | null;
  size?: { w: number; h: number } | null;
  shapes?: string[];
  shapes_dropped?: string[];
  /** Family Tape Rescue: the family page (once made), what was found on the tape, and about how long it takes. */
  family_url?: string | null;
  scene_count?: number | null;
  kept_seconds?: number | null;
  removed_seconds?: number | null;
  minutes?: number | null;
}

/** A rescued tape's family page (/family/:id): every scene plays and downloads straight from where it is kept. */
export interface FamilyInfo {
  id: string;
  title: string;
  created_at?: string;
  tape_seconds?: number;
  kept_seconds?: number;
  removed_seconds?: number;
  interlaced?: boolean;
  best: string;
  poster: string;
  size?: { w: number; h: number } | null;
  scenes: { n: number; start: number; end: number; video: string; still: string }[];
}

export async function getFamily(id: string): Promise<FamilyInfo | null> {
  const r = await fetch(`/api/family/${id}`, { headers: { Accept: 'application/json' } }).catch(() => null);
  return r && r.ok ? ((await r.json()) as FamilyInfo) : null;
}

export interface CoverInfo {
  id: string;
  /** Promote your new song (2026-10-19): where people can listen, and the song's name. */
  listen?: string | null;
  listen_service?: { id: string; name: string } | null;
  title?: string | null;
  song: boolean;
  beats: number[];
  /** The other bass hits (a shake each), on covers made since 2026-10-18. */
  shakes?: { t: number; s: number }[];
  video_url: string;
  poster_url: string;
  /** The buyer's own picture in the cover's shape (the "Without" beside it); older covers: the poster with the look undone. */
  before_url?: string;
  /** What the page draws floating over the video; null on covers made before 2026-10-17 (drawn into the video). */
  float?: string[] | null;
  amount?: string;
  /** Each overlay's own see-through slider ({ kind: 0..1 }), on covers made since 2026-10-21. */
  opacity?: Record<string, number> | null;
  /** Where each overlay sits ({ kind: { x, y, s } }, float-scene placeBox), on covers made since 2026-10-24. */
  spot?: Record<string, { x: number; y: number; s: number }> | null;
  /** The cover's shape when it is not 1080x1920 (owner 2026-10-23); absent on older covers. */
  size?: { w: number; h: number } | null;
  /** How dark the cover's picture is, 0..1 (canvas.js DARK): the live flash lifts more; absent on older covers. */
  dark?: number;
  title?: string;
}

export async function getCover(id: string): Promise<CoverInfo | null> {
  const r = await fetch(`/api/cover/${id}`, { headers: { Accept: 'application/json' } }).catch(() => null);
  return r && r.ok ? ((await r.json()) as CoverInfo) : null;
}

/** The Living Cover examples the owner picked (shown on the tool page and the home page). */
let examplesCache: Promise<CoverInfo[]> | null = null;
export function getCoverExamples(fresh = false): Promise<CoverInfo[]> {
  if (!examplesCache || fresh) {
    examplesCache = fetch('/api/canvas/examples', { headers: { Accept: 'application/json' }, cache: fresh ? 'no-store' : 'default' })
      .then((r) => (r.ok ? r.json() : { examples: [] }))
      .then((j: { examples?: CoverInfo[] }) => j.examples || [])
      .catch(() => []);
  }
  return examplesCache;
}
/**
 * The Living Cover's depth for a picked picture (POST /api/canvas/depth-preview, canvas.js depthPreview): where the
 * subject leans from and breathes, as shares of the frame, and the layers the preview draws (data URLs).
 */
export interface DepthPreview {
  depth: boolean;
  why?: string;
  ax?: number;
  ay?: number;
  breath?: { ys: number; yc: number } | null;
  mask?: string;
  inv?: string;
  halo?: string;
  plate?: string;
  /** Where the subject is, as shares of the frame [x0, y0, x1, y1]. */
  box?: number[] | null;
  /** In a shape (owner 2026-10-23): the shape, its size, where the picture was placed, and the frame's picture (data URL). */
  shape?: string;
  size?: { w: number; h: number };
  placed?: { mode: 'fill' | 'crop'; pos: number; filled?: number; zoom?: number; focus?: number };
  still?: string;
  /** What the picture is of, when the page did not say (owner 2026-10-23, "Here's what we found"). */
  suggest?: 'person' | 'object' | 'scene' | null;
  /** A face was found (the wind in the hair is offered). */
  face?: boolean;
  /** Standing on ground that carries on beside them: the walk across can be offered (stance.py). */
  walk?: boolean;
  /** Found where the buyer tapped or roughly painted (the check had missed it). */
  tapped?: boolean;
  /** Their rough paint: how sure each stroke's outline is (0..1), and the strokes it could not settle (owner 2026-10-26). */
  paint?: { sure: number[]; unsure: number[] } | null;
  /** How dark the picture is, 0..1 (canvas.js DARK): the page eases its look the same way the file will. */
  dark?: number;
  /** The sky (sky.py): found or not, and what stays in front of a new one (white, its alpha the front). */
  /** The sky found, or the part the buyer painted to replace (painted): front is what stays in front of it, mask the part itself. */
  sky?: { found: boolean; painted?: boolean; front?: string; mask?: string; why?: string };
  /** The server keeps this check's work a while; the motion preview reuses it with this token. */
  token?: string;
}
/** The new motion's preview (canvas.js motionPreview): a small loop of the picture moving, as a data URL. */
export interface MotionPreview {
  video: string | null;
  why?: string;
  note?: { sky: string | null; float: boolean; hair: boolean; paint: string[] };
}
/** Each shape's picture, as the cover will be drawn from it (filled around, or the buyer's window). */
export interface FramePreview {
  picture: { w: number; h: number };
  shapes: Record<string, { size: { w: number; h: number }; placed: { mode: 'fill' | 'crop'; pos: number; why?: string; filled?: number; zoom?: number; focus?: number }; still: string }>;
}
/**
 * The picture cover-fit to the cover's working size (1350x2400) as a JPEG: a phone photo goes up small, cropped as the
 * file will be. A PNG stays a PNG: one already cut out (a see-through background, ChopBox's Remove background) keeps
 * its cut, and the server uses that outline exactly (owner 2026-10-21).
 */
async function coverFitJpeg(file: File): Promise<Blob> {
  const png = file.type === 'image/png';
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const W = 1350;
    const H = 2400;
    const c = Object.assign(document.createElement('canvas'), { width: W, height: H });
    const k = Math.max(W / img.naturalWidth, H / img.naturalHeight);
    const w = img.naturalWidth * k;
    const h = img.naturalHeight * k;
    c.getContext('2d')?.drawImage(img, (W - w) / 2, (H - h) / 2, w, h);
    return await new Promise<Blob>((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error('no blob'))), png ? 'image/png' : 'image/jpeg', 0.9));
  } finally {
    URL.revokeObjectURL(url);
  }
}
/**
 * The picture whole, at most `long` px on its long side (never cut: the shapes are made from all of it, owner
 * 2026-10-23), as a JPEG, or a PNG when it is one (a picture already cut out keeps its see-through background).
 */
async function wholeJpeg(file: File, long = 1600): Promise<Blob> {
  const png = file.type === 'image/png';
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    const k = Math.min(1, long / Math.max(img.naturalWidth, img.naturalHeight));
    const W = Math.max(1, Math.round(img.naturalWidth * k));
    const H = Math.max(1, Math.round(img.naturalHeight * k));
    const c = Object.assign(document.createElement('canvas'), { width: W, height: H });
    c.getContext('2d')?.drawImage(img, 0, 0, W, H);
    return await new Promise<Blob>((resolve, reject) => c.toBlob((b) => (b ? resolve(b) : reject(new Error('no blob'))), png ? 'image/png' : 'image/jpeg', 0.9));
  } finally {
    URL.revokeObjectURL(url);
  }
}
/**
 * THE SHAPES, PREVIEWED (owner 2026-10-23): the picture in each shape as the cover will be drawn from it, from the
 * server (POST /api/canvas/frame-preview). `place`: the buyer's placements ("9x16:crop:0.4,..."); the others automatic.
 * Null when the server could not answer (busy for long, offline).
 */
export async function getFramePreview(file: File, subject: string, shapes: string[], place: string, signal?: AbortSignal, tap = ''): Promise<FramePreview | null> {
  let body: Blob;
  try {
    body = await wholeJpeg(file);
  } catch {
    body = file;
  }
  for (let tries = 0; tries < 6; tries++) {
    const fd = new FormData();
    fd.append('subject', subject || '');
    fd.append('shapes', shapes.join(','));
    fd.append('place', place || '');
    if (tap) fd.append('tap', tap);
    fd.append('media', body, body.type === 'image/png' ? 'picture.png' : 'picture.jpg');
    const r = await fetch('/api/canvas/frame-preview', { method: 'POST', body: fd, signal: timed(signal) }).catch(() => null);
    if (signal?.aborted) return null;
    if (r && r.ok) {
      const j = (await r.json().catch(() => null)) as FramePreview | null;
      return j && j.shapes ? j : null;
    }
    if (!r || r.status !== 503) return null;
    // Busy: another preview of this page (a few seconds), or a cover being made (minutes).
    await new Promise((res) => setTimeout(res, tries < 3 ? 3000 : 15000));
    if (signal?.aborted) return null;
  }
  return null;
}
/**
 * The server's answer, { depth: false, why } included when it found no clear subject (the page says so when a live
 * backdrop is picked); null when it could not answer (busy, offline).
 */
/** The page's own signal, plus a time limit: a preview the server never answers ends as "could not" instead of a spinner
 *  that never stops (owner 2026-10-28: "the tool has been hung loading for the last 5 minutes"). */
const PREVIEW_LIMIT_MS = 120_000;
function timed(signal?: AbortSignal): AbortSignal {
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), PREVIEW_LIMIT_MS);
  const stop = () => { clearTimeout(timer); ac.abort(); };
  if (signal) {
    if (signal.aborted) stop();
    else signal.addEventListener('abort', stop, { once: true });
  }
  return ac.signal;
}
/** The crop brush's pictures (cut-edit.ts): every line brushed, for what stays and for the part replaced. */
export type CutEdits = { tap: Blob | null; replace: Blob | null };
export async function getDepthPreview(file: File, subject: string, signal?: AbortSignal, frame?: { shape: string; place: string } | null, tap = '', replace = '', force = false, edits: CutEdits | null = null): Promise<DepthPreview | null> {
  let body: Blob;
  try {
    // In a shape the server places the picture itself (filled around, never cut), so it gets the whole picture.
    body = frame ? await wholeJpeg(file) : await coverFitJpeg(file);
  } catch {
    body = file;
  }
  // A cover being made keeps the server busy for a while: ask again a few times, then show the flat preview.
  for (let tries = 0; tries < 6; tries++) {
    const fd = new FormData();
    fd.append('subject', subject || '');
    if (frame) {
      fd.append('shape', frame.shape);
      fd.append('place', frame.place);
    }
    // Where the buyer tapped the subject the check missed (0..1 of the picture).
    if (tap) fd.append('tap', tap);
    // The part painted to be replaced by a new sky or scene (owner 2026-10-27), strokes as the tap's.
    if (replace) fd.append('replace', replace);
    // The buyer chose to remove the background: the model's best subject is cut out, always (owner 2026-10-27).
    if (force) fd.append('force', '1');
    if (edits?.tap) fd.append('tapedit', edits.tap, 'tapedit.png');
    if (edits?.replace) fd.append('replaceedit', edits.replace, 'replaceedit.png');
    fd.append('media', body, body.type === 'image/png' ? 'picture.png' : 'picture.jpg');
    const r = await fetch('/api/canvas/depth-preview', { method: 'POST', body: fd, signal: timed(signal) }).catch(() => null);
    if (signal?.aborted) return null;
    if (r && r.ok) {
      const j = (await r.json().catch(() => null)) as DepthPreview | null;
      return j && typeof j.depth === 'boolean' ? j : null;
    }
    if (!r || r.status !== 503) return null;
    // Busy: the page's shape preview (a few seconds), or a cover being made (minutes).
    await new Promise((res) => setTimeout(res, tries < 3 ? 3000 : 20000));
    if (signal?.aborted) return null;
  }
  return null;
}
/**
 * The new motion previewed by the cover's own engine (POST /api/canvas/motion-preview): with the picture check's token
 * the server reuses its work; when that has expired (410) the picture is sent again. null when it could not answer.
 */
/** A data: URL as a blob: link (decoded in place; nothing is fetched). */
function dataUrlToBlobUrl(url: string): string {
  const comma = url.indexOf(',');
  const type = url.slice(5, comma).split(';')[0] || 'application/octet-stream';
  const bin = atob(url.slice(comma + 1));
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return URL.createObjectURL(new Blob([bytes], { type }));
}
export async function getMotionPreview(file: File, fields: Record<string, string>, token: string | null | undefined, frame: { shape: string; place: string } | null, signal?: AbortSignal, edits: CutEdits | null = null): Promise<MotionPreview | null> {
  const ask = async (withFile: boolean) => {
    const fd = new FormData();
    for (const [k, v] of Object.entries(fields)) fd.append(k, v);
    if (frame) {
      fd.append('shape', frame.shape);
      fd.append('place', frame.place);
    }
    if (token && !withFile) fd.append('token', token);
    if (withFile) {
      let body: Blob;
      try { body = frame ? await wholeJpeg(file) : await coverFitJpeg(file); } catch { body = file; }
      fd.append('media', body, body.type === 'image/png' ? 'picture.png' : 'picture.jpg');
      if (edits?.tap) fd.append('tapedit', edits.tap, 'tapedit.png');
      if (edits?.replace) fd.append('replaceedit', edits.replace, 'replaceedit.png');
    }
    return fetch('/api/canvas/motion-preview', { method: 'POST', body: fd, signal: timed(signal) }).catch(() => null);
  };
  for (let tries = 0; tries < 6; tries++) {
    let r = await ask(!token);
    if (signal?.aborted) return null;
    if (r && r.status === 410) r = await ask(true);
    if (signal?.aborted) return null;
    if (r && r.ok) {
      const j = (await r.json().catch(() => null)) as MotionPreview | null;
      if (!j || !('video' in j)) return null;
      // As a blob: link: the site's content policy plays media from https: and blob: only, so the server's data: video
      // was refused on the live site and the preview went blank (owner 2026-10-24, his sky crop). The caller revokes it.
      // Decoded here, not fetched: the same policy keeps fetch() off data: links too.
      if (j.video && j.video.startsWith('data:')) j.video = dataUrlToBlobUrl(j.video);
      return j;
    }
    if (!r || r.status !== 503) return null;
    // Busy (a cover being made): ask again shortly.
    await new Promise((res) => setTimeout(res, tries < 3 ? 3000 : 15000));
    if (signal?.aborted) return null;
  }
  return null;
}
/** Is this account an admin (may pick examples)? */
export async function isExamplesAdmin(token: string): Promise<boolean> {
  const r = await fetch('/api/canvas/examples/admin', { headers: { Authorization: `Bearer ${token}` } }).catch(() => null);
  return Boolean(r && r.ok);
}
export async function addCoverExample(jobId: string, token: string): Promise<boolean> {
  const r = await fetch('/api/canvas/examples', { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ job_id: jobId }) }).catch(() => null);
  return Boolean(r && r.ok);
}
export async function removeCoverExample(coverId: string, token: string): Promise<boolean> {
  const r = await fetch(`/api/canvas/examples/${coverId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }).catch(() => null);
  return Boolean(r && r.ok);
}

export interface ApiError {
  error: string;
  message?: string;
}

let configCache: Promise<Record<QuickToolKey, QuickToolConfig>> | null = null;

/** Prices (env-set, so they change without a deploy) and the faceless voices. */
export function getQuickToolsConfig(): Promise<Record<QuickToolKey, QuickToolConfig>> {
  if (!configCache) {
    configCache = fetch('/api/quicktools/config', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`config ${r.status}`))))
      .then((j) => j.tools as Record<QuickToolKey, QuickToolConfig>)
      .catch((e) => {
        configCache = null;
        throw e;
      });
  }
  return configCache;
}

export const money = (cents: number) => `$${(cents / 100).toFixed(cents % 100 ? 2 : 0)}`;

const api = (key: QuickToolKey) => `/api/${key}/jobs`;

export async function getJob(key: QuickToolKey, id: string, check = false, sessionId?: string | null): Promise<{ ok: boolean; job?: QuickJob; err?: ApiError }> {
  const q = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : check ? '?check=1' : '';
  const r = await fetch(`${api(key)}/${id}${q}`, { cache: 'no-store' });
  const j = await r.json().catch(() => ({}));
  return r.ok ? { ok: true, job: j as QuickJob } : { ok: false, err: j as ApiError };
}

/** Multipart upload with progress. Resolves with the new job or the server's refusal. */
export function uploadJob(
  key: QuickToolKey,
  form: FormData,
  onProgress: (fraction: number) => void,
): Promise<{ ok: boolean; job?: QuickJob; err?: ApiError }> {
  return new Promise((resolve) => {
    const x = new XMLHttpRequest();
    x.open('POST', api(key));
    // Signed in: the file lands in this account's gallery once paid and made.
    try {
      const token = localStorage.getItem('auth_token');
      if (token) x.setRequestHeader('Authorization', `Bearer ${token}`);
    } catch {
      /* storage blocked: upload without an account */
    }
    x.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    x.onload = () => {
      let j: unknown = {};
      try {
        j = JSON.parse(x.responseText);
      } catch {
        /* not JSON: a proxy error page */
      }
      if (x.status === 201 && (j as QuickJob).id) resolve({ ok: true, job: j as QuickJob });
      else resolve({ ok: false, err: (j as ApiError).error ? (j as ApiError) : { error: 'upload_failed' } });
    };
    x.onerror = () => resolve({ ok: false, err: { error: 'network' } });
    x.send(form);
  });
}

export async function startCheckout(
  key: QuickToolKey,
  id: string,
  inPerson?: { operator_key: string; price_cents: number },
): Promise<{ url?: string; qr?: string | null; amount_cents?: number; already_paid?: boolean; error?: string; message?: string }> {
  const r = await fetch(`${api(key)}/${id}/checkout`, {
    method: 'POST',
    headers: inPerson ? { 'Content-Type': 'application/json' } : undefined,
    body: inPerson ? JSON.stringify(inPerson) : undefined,
  });
  return r.json().catch(() => ({ error: 'checkout_failed' }));
}

/** Signed in: pay for the file from the credit balance; the server starts making it. */
export async function payWithCredits(key: QuickToolKey, id: string, token: string): Promise<{ ok: boolean; job?: QuickJob; err?: ApiError & { need?: number } }> {
  const r = await fetch(`${api(key)}/${id}/pay-credits`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
  const j = await r.json().catch(() => ({}));
  return r.ok ? { ok: true, job: j as QuickJob } : { ok: false, err: j as ApiError };
}

/** Make a paid file again without the site mark (free). */
export async function remakeWithoutMark(key: QuickToolKey, id: string): Promise<{ ok: boolean; job?: QuickJob; err?: ApiError }> {
  const r = await fetch(`${api(key)}/${id}/remake`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }).catch(() => null);
  if (!r) return { ok: false, err: { error: 'network' } };
  const j = await r.json().catch(() => ({}));
  return r.ok ? { ok: true, job: j as QuickJob } : { ok: false, err: j as ApiError };
}

export const previewUrl = (key: QuickToolKey, id: string) => `${api(key)}/${id}/preview`;

/** `inline`: to play in the page instead of saving; `v`: a new link once the file is made again. */
export function downloadUrl(key: QuickToolKey, id: string, opts: { sessionId?: string | null; operatorKey?: string | null; file?: string; inline?: boolean; v?: string } = {}) {
  const q = new URLSearchParams();
  if (opts.sessionId) q.set('session_id', opts.sessionId);
  if (opts.operatorKey) q.set('operator_key', opts.operatorKey);
  if (opts.file) q.set('file', opts.file);
  if (opts.inline) q.set('inline', '1');
  if (opts.v) q.set('v', opts.v);
  const qs = q.toString();
  return `${api(key)}/${id}/download${qs ? `?${qs}` : ''}`;
}

// RECENT FILES ON THIS DEVICE, for /projects: the job link is the only key,
// so it is kept here (never sent anywhere) until the server sweeps the job.
const RECENT_KEY = 'quicktools:recent-v1';
const RECENT_TTL_MS = 48 * 3600 * 1000;

export interface RecentQuickJob {
  tool: QuickToolKey;
  id: string;
  at: number;
}

export function rememberQuickJob(tool: QuickToolKey, id: string) {
  try {
    const list = recentQuickJobs().filter((j) => j.id !== id);
    list.unshift({ tool, id, at: Date.now() });
    window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 20)));
  } catch {
    /* storage blocked: the page link still works */
  }
}

export function recentQuickJobs(): RecentQuickJob[] {
  try {
    const raw = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]') as RecentQuickJob[];
    return Array.isArray(raw) ? raw.filter((j) => j && /^[a-f0-9]{32}$/.test(j.id) && Date.now() - j.at < RECENT_TTL_MS) : [];
  } catch {
    return [];
  }
}

// IN-PERSON SELLING: the seller opens any tool once with ?op=<key>; the key
// stays on that phone only (the server checks it on every in-person call).
const OP_KEY = 'ac_op';

export function readOperatorKey(search: string): string | null {
  try {
    const q = new URLSearchParams(search);
    const given = q.get('op');
    if (given) window.localStorage.setItem(OP_KEY, given);
    return window.localStorage.getItem(OP_KEY);
  } catch {
    return null;
  }
}
